'use client'

import { setInitialMessage } from '@/lib/features/copilotv2/copilotInitialMessageStorage'
import { getToolDisplay } from '@/utils/copilotToolMappings'
import { useRouter } from 'next/navigation'
import { useCallback } from 'react'
import type { ScheduledTaskToolResult } from './scheduledTasksApi'

/**
 * Extracts a workflow execution ID from a scheduled task tool result payload.
 * Supports direct workflow tools (result.executionId) and template tools (result.nodeExecutionIds).
 */
export function extractWorkflowExecutionId(result: Record<string, unknown> | null): string | null {
  if (!result) return null

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

  const normalizeWorkflowExecutionId = (id: string): string | null => {
    const trimmed = id.trim()
    if (!trimmed) return null

    // Scheduled-task execution IDs (BullMQ job IDs) should never be treated as workflow executions.
    if (trimmed.startsWith('stx_')) return null

    if (trimmed.startsWith('workflow_execution_')) return trimmed
    if (uuidRegex.test(trimmed)) return `workflow_execution_${trimmed}`

    return null
  }

  // Unwrap the ToolResult wrapper: { success: true, result: { ... } }
  const inner =
    result.success === true && result.result && typeof result.result === 'object'
      ? (result.result as Record<string, unknown>)
      : result

  // Some tools return workflowExecutionId explicitly.
  if (typeof inner.workflowExecutionId === 'string') {
    const normalized = normalizeWorkflowExecutionId(inner.workflowExecutionId)
    if (normalized) return normalized
  }

  // Direct workflow tools: result.executionId
  if (typeof inner.executionId === 'string' && inner.executionId) {
    const normalized = normalizeWorkflowExecutionId(inner.executionId)
    if (normalized) return normalized
  }

  // Template tools: parse the first node execution ID from nodeExecutionIds string.
  // Format: "NodeName:node_execution_<workflowExecUuid>_<nodeExecUuid>, ..."
  const nodeExecutionIdsRaw = inner.nodeExecutionIds
  const nodeExecutionIdsString =
    typeof nodeExecutionIdsRaw === 'string'
      ? nodeExecutionIdsRaw
      : Array.isArray(nodeExecutionIdsRaw) &&
          nodeExecutionIdsRaw.length > 0 &&
          typeof nodeExecutionIdsRaw[0] === 'string'
        ? nodeExecutionIdsRaw[0]
        : null

  if (nodeExecutionIdsString) {
    const firstEntry = nodeExecutionIdsString.split(',')[0]?.trim()
    if (firstEntry) {
      const colonIdx = firstEntry.indexOf(':')
      const nodeExecId = colonIdx >= 0 ? firstEntry.slice(colonIdx + 1).trim() : firstEntry.trim()
      const match = nodeExecId.match(/^node_execution_([0-9a-f-]+)_[0-9a-f-]+$/i)
      if (match?.[1]) {
        return `workflow_execution_${match[1]}`
      }
    }
  }

  return null
}

interface OpenExecutionOptions {
  workspaceId: string
  /** Tool results for this run. When omitted, fetched from the backend automatically. */
  results?: ScheduledTaskToolResult[]
  /** Included in the initial copilot summary message. Only relevant when results are provided. */
  taskName?: string
}

/**
 * Hook providing centralized logic for opening a scheduled task execution in the Copilot.
 * Callers manage their own loading state and error handling; this hook throws on failure.
 *
 * openExecution handles both single-result and full-run cases:
 * - Pass results=[singleResult] to open one tool result
 * - Pass the full results array (+ taskName) to open an entire run with a summary message
 * - Omit results to auto-fetch them (e.g. from an email notification link with only the stx_ ID)
 *
 * If any result contains a workflow execution ID, routes through /results/ so the full
 * execution is loaded into IndexedDB and the Copilot renders rich workflow output.
 * Otherwise creates a session seeded with the run's tool interactions directly.
 */
export function useOpenScheduledTaskInCopilot() {
  const router = useRouter()

  const openExecution = useCallback(
    async (
      scheduledTaskExecutionId: string,
      { workspaceId, results: providedResults, taskName }: OpenExecutionOptions
    ) => {
      // Auto-fetch results when not provided (e.g. email link with only the stx_ ID),
      // so we can apply the same workflow-routing logic as the modal.
      let results = providedResults
      if (!results) {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/scheduled-tasks/executions/${encodeURIComponent(scheduledTaskExecutionId)}`,
          { credentials: 'include' }
        )
        if (res.ok) {
          results = ((await res.json()) as { results: ScheduledTaskToolResult[] }).results
        }
      }

      // If any tool in this run produced a workflow execution, route through /results/
      // so node executions are loaded into IndexedDB and the Copilot shows rich output.
      if (results) {
        const wfExecIds = new Set<string>()
        for (const r of results) {
          if (r.result) {
            const id = extractWorkflowExecutionId(r.result)
            if (id) wfExecIds.add(id)
          }
        }
        if (wfExecIds.size > 0) {
          router.push(`/results/${encodeURIComponent([...wfExecIds].join(','))}`)
          return
        }
      }

      // Non-workflow run: create a session with backend-seeded tool interactions.
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/copilot/session`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ workspaceId, scheduledTaskExecutionId }),
        }
      )
      if (!res.ok) throw new Error('Failed to create session')
      const { session } = (await res.json()) as { session: { id: string } }

      if (results && taskName) {
        const lines = [
          `Scheduled task "${taskName}" — ${results.length} tool(s) executed.`,
          '',
          ...results.map(
            (r, i) =>
              `${i + 1}. ${getToolDisplay(r.toolName).displayName}: ${r.status}${r.error ? ` — ${r.error}` : ''}`
          ),
        ]
        await setInitialMessage(session.id, { message: lines.join('\n') })
      }

      router.replace(`/copilot/${session.id}`)
    },
    [router]
  )

  return { openExecution }
}
